import {
  articles,
  categoryMetas,
  type Article,
  type ArticleCategory,
} from "@/mocks/articles";

export const PAGE_SIZE = 12;

export function getCategoryMeta(id: ArticleCategory) {
  return categoryMetas.find((c) => c.id === id);
}

export function categoryName(id: ArticleCategory): string {
  return getCategoryMeta(id)?.name ?? "综合";
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((a) => a.id === id);
}

export function getArticlesByCategory(category: ArticleCategory): Article[] {
  return articles.filter((a) => a.category === category);
}

export function getFeaturedArticles(limit = 3): Article[] {
  const featured = articles.filter((a) => a.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  return [...featured, ...articles.filter((a) => !a.featured)].slice(0, limit);
}

export function getLatestArticles(limit = 10): Article[] {
  return [...articles]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);
}

export function getPopularArticles(limit = 4): Article[] {
  return [...articles]
    .sort((a, b) => b.views + b.favorites * 5 - (a.views + a.favorites * 5))
    .slice(0, limit);
}

export function getRelatedArticles(article: Article, limit = 3): Article[] {
  return getArticlesByCategory(article.category)
    .filter((a) => a.id !== article.id)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);
}

export function searchArticles(
  keyword: string,
  category: ArticleCategory | "all" = "all",
): Article[] {
  const kw = keyword.trim().toLowerCase();
  return articles.filter((a) => {
    if (category !== "all" && a.category !== category) return false;
    if (!kw) return true;
    return (
      a.title.toLowerCase().includes(kw) ||
      a.summary.toLowerCase().includes(kw) ||
      a.author.toLowerCase().includes(kw) ||
      a.tags.some((t) => t.toLowerCase().includes(kw)) ||
      a.content.some((block) =>
        block.type === "image"
          ? block.alt.toLowerCase().includes(kw)
          : block.text.toLowerCase().includes(kw),
      )
    );
  });
}

export function formatDate(date: string): string {
  return date.replace(/-/g, ".");
}

export function formatCount(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

export function getYears(category?: ArticleCategory): number[] {
  const list = category ? getArticlesByCategory(category) : articles;
  const years = Array.from(new Set(list.map((a) => Number(a.date.slice(0, 4)))));
  return years.sort((a, b) => b - a);
}

export function getTags(category?: ArticleCategory): string[] {
  const list = category ? getArticlesByCategory(category) : articles;
  return Array.from(new Set(list.flatMap((a) => a.tags)));
}